import { Action, createSlice, PayloadAction, ThunkAction } from '@reduxjs/toolkit';
import Cookies from 'js-cookie';
import { AxiosError } from 'axios';
import { enqueueSnackbar } from 'notistack';
import { FormattedMessage } from 'react-intl';

// common
import { AUTHENTICATION_TOKEN } from 'common/constants';
import { isGuestToken } from 'common/hook/useIsGuest';
import { AppState } from '@redux/store';
import { apiClient } from 'api/apiClient';
import { type UserDetail } from 'modules/account/services/useGetAccountInfo';
import type { ReplaceUserRoles } from 'modules/account/types';

type ThunkPromise = ThunkAction<Promise<void>, AppState, null, Action>;

/**
 * Các HTTP status CÓ câu dịch riêng trong `error.status` (intl/translate/vi.json). Status ngoài
 * danh sách này (và cả khi không có response) được quy về khoá chung, thay vì ghép thẳng
 * `error.status.<số>` rồi hiện id trơ ra trên snackbar — đúng lỗi đã gặp: 401 chưa có câu dịch nên
 * người dùng nhìn thấy nguyên chữ "error.status.401".
 */
const TRANSLATED_ERROR_STATUSES = [400, 401, 403, 404, 429, 500, 502, 503, 504];

export const errorStatusMessageId = (status?: number): string => {
  if (status == null) return 'error.status.network';
  return TRANSLATED_ERROR_STATUSES.includes(status)
    ? `error.status.${status}`
    : 'error.status.unknown';
};

/** Ném ra khi BE trả 2xx nhưng thân thiếu token — xem logInAsGuest. */
export const GUEST_TOKEN_MISSING = 'GUEST_TOKEN_MISSING';

/**
 * Thông báo cho nút "Tiếp tục với tư cách Khách". Tách riêng 429 (vượt trần cấp token khách theo
 * IP — chờ một lúc là vào được) khỏi các lỗi còn lại (mất mạng, CORS, BE lỗi — không tự hết), vì
 * gộp chung một câu "vui lòng thử lại sau" khiến người dùng chờ vô ích trong trường hợp thứ hai.
 */
export const guestLoginErrorMessageId = (error: unknown): string =>
  error instanceof AxiosError && error.response?.status === 429
    ? 'continueAsGuestTooMany'
    : 'continueAsGuestError';

// type UserRoles = typeof schemas.RoleAssignments._type;
type UserRoles = ReplaceUserRoles;

/** Request payload for POST login - mirrors LoginReqData schema */
export interface ReplaceLoginReqData {
  handle: string;
  // min(8).max(72)
  password: string;
  [key: string]: unknown;
}

// export type LoginReqData = typeof schemas.LoginReqData._type;
export type LoginReqData = ReplaceLoginReqData;

export interface AuthState {
  isLogin: boolean;
  loading: boolean;
  validate: boolean;
  userInfo?: UserDetail;
  userRoles: UserRoles;
  /** Tài khoản phụ huynh đã login nhưng CHƯA nhập Mã xác nhận → hiện popup trên trang login. */
  pendingConfirmationCode: boolean;
}

export const initialStateAuth: AuthState = {
  isLogin: false,
  loading: false,
  validate: true,
  userRoles: {} as UserRoles,
  pendingConfirmationCode: false,
};

export const authSlice = createSlice({
  name: 'auth',
  initialState: initialStateAuth,
  reducers: {
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    authIn: (state) => {
      state.isLogin = true;
    },
    authOut: (state) => {
      state.isLogin = false;
      state.userInfo = undefined;
      state.pendingConfirmationCode = false;
      localStorage.clear();
    },
    setUserInfo: (state, action: PayloadAction<UserDetail | undefined>) => {
      state.userInfo = action.payload;
    },
    setUserRoles: (state, action: PayloadAction<UserRoles>) => {
      state.userRoles = action.payload;
    },
    setPendingConfirmationCode: (state, action: PayloadAction<boolean>) => {
      state.pendingConfirmationCode = action.payload;
    },
  },
});

export const { authIn, authOut, setLoading, setUserInfo, setUserRoles, setPendingConfirmationCode } =
  authSlice.actions;

export function getUserInfo(): ThunkPromise {
  return async (dispatch) => {
    try {
      if (!Cookies.get(AUTHENTICATION_TOKEN)) {
        await dispatch(logoutFnc());
        return;
      }
      const userInfo = await apiClient().UserController_getData();
      dispatch(authIn());
      dispatch(setUserInfo(userInfo));
    } catch (e) {
      console.error('e: ', e);
      dispatch(authOut());
    }
  };
}

export function logIn({ handle, password }: LoginReqData): ThunkPromise {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      const cookie = await apiClient().AuthenController_loginWithPassword({
        handle,
        password,
      });
      if (!cookie.token) return;
      Cookies.set(AUTHENTICATION_TOKEN, cookie.token);

      // Phụ huynh (KH-PHOTHONG): BE cấp token hạn chế + chặn mọi API dữ liệu tới khi nhập Mã xác
      // nhận. DỪNG ở đây (không gọi getData/getRoleAssignments — BE sẽ 403), hiện popup trên login.
      if (cookie.requiresConfirmationCode) {
        dispatch(setPendingConfirmationCode(true));
        dispatch(setLoading(false));
        return;
      }

      const user = await apiClient().UserController_getData();
      const userRoles = await apiClient().UserController_getRoleAssignments();

      dispatch(authIn());
      dispatch(setUserInfo(user));
      dispatch(setUserRoles(userRoles));
    } catch (error) {
      dispatch(setLoading(false));
      if (error instanceof AxiosError) {
        throw enqueueSnackbar({
          message: <FormattedMessage id={errorStatusMessageId(error.response?.status)} />,
          variant: 'error',
        });
      }
      console.error(error);
    } finally {
      dispatch(setLoading(false));
    }
  };
}

/**
 * "Tiếp tục với tư cách Khách": lấy token khách rồi vào app luôn. KHÔNG gọi getData/
 * getRoleAssignments — khách bị BE chặn (403 GUEST_FORBIDDEN) vì 2 endpoint đó không nằm trong
 * allowlist; userInfo/userRoles để rỗng nên menu quản trị tự ẩn.
 *
 * Mọi thất bại đều báo qua enqueueSnackbar (không để rơi vào unhandled rejection) và finally đảm
 * bảo nút không kẹt trạng thái loading. Câu thông báo do guestLoginErrorMessageId chọn: 429
 * (vượt trần cấp token khách theo IP) tách riêng khỏi lỗi mạng/CORS/BE lỗi.
 */
export function logInAsGuest(): ThunkPromise {
  return async (dispatch) => {
    dispatch(setLoading(true));
    try {
      const rs = await apiClient().AuthenController_loginAsGuest();
      // 2xx mà thiếu token thì vẫn là thất bại. Trước đây `return` im lặng ở đây: nút chỉ nhá
      // loading rồi thôi, không thông báo gì — người dùng tưởng bấm không ăn.
      if (!rs?.token) throw new Error(GUEST_TOKEN_MISSING);
      Cookies.set(AUTHENTICATION_TOKEN, rs.token);

      // Khách không được thừa hưởng userInfo/userRoles của phiên người dùng thật trước đó còn sót
      // lại trong redux-persist (persist:root) — nếu không dọn, menu (vd nhóm "Quản trị hệ thống")
      // có thể vẫn hiện ra vì routesByUserRoles() đọc theo roles cũ đó.
      dispatch(setUserInfo(initialStateAuth.userInfo));
      dispatch(setUserRoles(initialStateAuth.userRoles));
      // Vào tư cách khách từ giữa luồng phụ huynh (đã đăng nhập, đang bị hỏi Mã xác nhận rồi bấm
      // "Tiếp tục với tư cách Khách"): phải tắt cờ chờ mã, nếu không nó còn đọng trong state đã
      // persist và popup nhập mã có thể nháy lại ở màn đăng nhập sau này.
      dispatch(setPendingConfirmationCode(false));

      dispatch(authIn());
    } catch (error) {
      console.error(error);
      enqueueSnackbar({
        message: <FormattedMessage id={guestLoginErrorMessageId(error)} />,
        variant: 'error',
      });
    } finally {
      dispatch(setLoading(false));
    }
  };
}

/**
 * Phụ huynh nộp Mã xác nhận sau khi login. Đúng → BE cấp token mới (codeValidated=true),
 * nạp user + vào app. Sai/hết hạn/khóa → apiClient throw (400/429) để popup hiển thị lỗi.
 */
export function verifyConfirmationCode(code: string): ThunkPromise {
  return async (dispatch) => {
    const rs = await apiClient().AuthenController_verifyConfirmationCode({ code });
    if (rs.token) Cookies.set(AUTHENTICATION_TOKEN, rs.token);

    const user = await apiClient().UserController_getData();
    const userRoles = await apiClient().UserController_getRoleAssignments();

    dispatch(setPendingConfirmationCode(false));
    dispatch(authIn());
    dispatch(setUserInfo(user));
    dispatch(setUserRoles(userRoles));
  };
}

/** "Quay lại": hủy nhập mã, xóa token hạn chế, về form login sạch. */
export function cancelConfirmationCode(): ThunkPromise {
  return async (dispatch) => {
    Cookies.remove(AUTHENTICATION_TOKEN);
    dispatch(setPendingConfirmationCode(false));
  };
}

export function revalidateAuthentication(): ThunkPromise {
  return async (dispatch) => {
    try {
      // Khách: không có profile/role để nạp — vào app luôn.
      if (isGuestToken()) {
        dispatch(authIn());
        return;
      }

      const user = await apiClient().UserController_getData();
      const userRoles = await apiClient().UserController_getRoleAssignments();
      dispatch(setUserInfo(user));
      dispatch(setUserRoles(userRoles));
      dispatch(authIn());
    } catch (error) {
      dispatch(setLoading(false));
      dispatch(authOut());
      if (error instanceof AxiosError) {
        throw enqueueSnackbar({
          message: <FormattedMessage id={errorStatusMessageId(error.response?.status)} />,
          variant: 'error',
        });
      }
      console.error(error);
    } finally {
      dispatch(setLoading(false));
    }
  };
}

export function logoutFnc(): ThunkPromise {
  return async (dispatch) => {
    dispatch(setLoading(true));
    try {
      if (!Cookies.get(AUTHENTICATION_TOKEN)) {
        dispatch(authOut());
        return;
      }

      await apiClient().UserController_logout(undefined);
    } catch (e) {
      console.error('e: ', e);
    } finally {
      // Dọn phiên cục bộ DÙ gọi API có lỗi hay không.
      //
      // Trước đây chỉ xoá cookie khi API trả success, và chính chỗ đó sinh ra vòng lặp vô hạn:
      // hàm này cũng là bộ xử lý 401 chung. Token hết hạn → API bất kỳ trả 401 → gọi logoutFnc →
      // gọi /authen-user/logout → request ĐÓ cũng 401 → lại gọi logoutFnc → cookie vẫn còn vì
      // chưa bao giờ success → lặp mãi (đo được 101 request logout liên tiếp).
      //
      // Người dùng bấm đăng xuất thì phải được đăng xuất, kể cả khi mạng hỏng hay phiên đã chết
      // ở server. Xoá cookie sau khi gọi API chứ không trước, để request còn mang token mà khai
      // tử phiên bên server.
      Cookies.remove(AUTHENTICATION_TOKEN);
      dispatch(authOut());
      dispatch(setLoading(false));
    }
  };
}

export default authSlice.reducer;
